import { accountSelector } from '../selector/account';
import { CONTACT_BANK_ERROR_CODES } from '../types';
import API from '@wetransfer/transfer/api';
import ExperimentDisablePaypal from '@wetransfer/transfer/constant/experiments/disable-paypal';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import scriptLoader from '@wetransfer/transfer/helper/script-loader';
import ActionHelper from '@wetransfer/transfer/modules/redux-actions';
import Router from '@wetransfer/transfer/modules/router';
import Notification from '@wetransfer/transfer/store/action/notification';
import PaymentAction from '@wetransfer/transfer/store/action/paymentError';
import Pending from '@wetransfer/transfer/store/action/pending';
import UserActions from '@wetransfer/transfer/store/action/user';

export const actions = ActionHelper.types([
  'GET_ACCOUNT',
  'CREATE_SUBDOMAIN',
  'GET_SUBSCRIPTION',
  'GET_CHECKOUT_DETAILS',
  'UPDATE_ACCOUNT_MEMBERSHIP_USER_EMAIL',
  'GET_UNPAID_INVOICES',

  'ACCOUNT_UPDATE_SUCCESS',
  'CANCEL_SUBSCRIPTION_SUCCESS',
  'REACTIVATE_SUBSCRIPTION_SUCCESS',
  'REVERT_ACCOUNT_SUCCESS',

  'PAYMENT_SUCCESS',
  'PAYMENT_ERROR',
  'PAYMENT_ERROR_RESET',

  'GET_PAID_UPGRADE_PREVIEW',
  'RESET_PAID_UPGRADE_PREVIEW',
]);

export default ActionHelper.generate({
  updateAccountMembershipUserEmail: [
    actions.UPDATE_ACCOUNT_MEMBERSHIP_USER_EMAIL,
    'email',
  ],

  getAccount(accountId) {
    const pendingId = 'getAccount';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getAccount(null, {
          pathExtValues: {
            accountId,
          },
        });

        dispatch({
          type: actions.GET_ACCOUNT,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_account_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  createSubdomain(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';
    const showNotification = Boolean(options.showNotification);

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.createSubdomain(params, {
          pathExtValues: {
            accountId,
          },
        });

        await dispatch({
          type: actions.CREATE_SUBDOMAIN,
          account: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  editAccount(accountId, params = {}, options = {}) {
    const pendingId = 'editAccount';

    const showNotification =
      options.notification === undefined || options.notification === true
        ? true
        : false;

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.editAccount(params, {
          pathExtValues: {
            accountId: accountId,
          },
        });

        dispatch({
          type: actions.ACCOUNT_UPDATE_SUCCESS,
          details: response.body,
        });

        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'notification',
              'update_profile_success'
            )
          );
        }
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification('error', 'update_profile_error')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getSubscription(accountId = null, subscriptionId = null, options = {}) {
    const pendingId = 'getSubscription';
    const showNotification = Boolean(options.showNotification);

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      let params;

      if (accountId) {
        params = { account_id: accountId };
      }

      try {
        const response = await API.Account.getSubscriptionDetails(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          details: response.body,
        });
      } catch (error) {
        if (showNotification) {
          dispatch(
            Notification.setNotification(
              'error',
              'retrieve_subscription_exception'
            )
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  cancelSubscription(params = {}) {
    const pendingId = 'cancelSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.cancelSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.CANCEL_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'cancel_subscription_success'
          )
        );
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'cancel_subscription_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  revertAccount(params = {}) {
    const pendingId = 'revertAccount';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.cancelSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });
        await dispatch(UserActions.getUser());

        dispatch({
          type: actions.REVERT_ACCOUNT_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'cancel_subscription_success'
          )
        );
        dispatch(Pending.resetPending(pendingId));
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'cancel_subscription_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  reactivateSubscription(params = {}) {
    const pendingId = 'reactivateSubscription';

    return async (dispatch, getState) => {
      dispatch(Pending.setPending(pendingId));

      const {
        account: { subscription },
      } = getState();

      try {
        const response = await API.Account.reactivateSubscription(params, {
          pathExtValues: {
            subscriptionId: subscription.id,
          },
        });

        dispatch({
          type: actions.REACTIVATE_SUBSCRIPTION_SUCCESS,
          details: response.body,
        });

        dispatch(
          Notification.setNotification(
            'notification',
            'reactivate_subscription_success'
          )
        );
      } catch (e) {
        dispatch(
          Notification.setNotification(
            'error',
            'reactivate_subscription_exception'
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  duplicateSubscription(params = {}, subscriptionId = null) {
    const pendingId = 'duplicateSubscription';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.duplicateSubscription(params, {
          pathExtValues: {
            subscriptionId,
          },
        });

        dispatch({
          type: actions.GET_SUBSCRIPTION,
          active: false,
          details: response.body,
        });

        Router.navigateTo('/payment/methods', false, {
          search: 'renewal=true',
        });
      } catch (e) {
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getCheckoutDetails(subscriptionId = null) {
    const pendingId = 'getCheckoutDetails';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getCheckoutDetails(null, {
          pathExtValues: {
            subscriptionId,
          },
        });

        /**
         * Ideally this should be in the backend
         * But there are few things missing in server side evaluations
         * like exposure tracking.
         * Hence in the interest of time, moving this to client side
         *
         * This should be removed once the experiment is over
         */
        if (ExperimentDisablePaypal.isPaypalDisabled()) {
          const { payment_methods } = response.body.checkout;
          response.body.checkout.payment_methods = payment_methods.filter(
            (methods) => methods !== 'paypal'
          );
        }

        dispatch({
          type: actions.GET_CHECKOUT_DETAILS,
          details: response.body.checkout,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification('error', 'retrieve_checkout_exception')
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  updatePaymentPlan(subscriptionId, newPlanId, couponCode) {
    const pendingId = 'updatePaymentPlan';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      //Overwrite  potential previous error
      dispatch(PaymentAction.setPaymentError(''));

      try {
        await API.Account.updatePaymentPlan(
          {
            new_nu_plan_id: newPlanId,
            coupon_code: couponCode,
          },
          {
            pathExtValues: {
              subscriptionId,
            },
          }
        );
      } catch (error) {
        dispatch(
          PaymentAction.setPaymentError({
            body: error?.body?.error,
            status: error.response?.status,
          })
        );

        //We do not want to display the generic error in case the error is about a coupon code
        if (
          error?.body?.error !== 'COUPON_CODE_NOT_COMPATIBLE_WITH_THIS_PLAN' &&
          error?.body?.error !== 'COUPON_NOT_EXIST'
        ) {
          dispatch(
            Notification.setNotification('error', 'something_went_wrong')
          );
        }
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getUnpaidInvoices() {
    const pendingId = 'getUnpaidInvoices';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Invoices.getUnpaidInvoices();

        dispatch({
          type: actions.GET_UNPAID_INVOICES,
          details: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification(
            'error',
            'retrieve_subscription_exception'
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  getPaidUpgradePreview({ plan_id }) {
    const pendingId = 'getPaidUpgradePreview';

    return async (dispatch) => {
      dispatch(Pending.setPending(pendingId));

      try {
        const response = await API.Account.getPaidUpgradePreview(null, {
          pathExtValues: { planId: plan_id },
        });

        dispatch({
          type: actions.GET_PAID_UPGRADE_PREVIEW,
          payload: response.body,
        });
      } catch (error) {
        dispatch(
          Notification.setNotification(
            'error',
            'retrieve_subscription_exception'
          )
        );
      }

      dispatch(Pending.resetPending(pendingId));
    };
  },

  resetPaidUpgradePreview: [actions.RESET_PAID_UPGRADE_PREVIEW],

  upgradePaidSubscription({ plan_id }) {
    const pendingId = 'upgradePaidSubscription';

    return async (dispatch, getState) => {
      const account = accountSelector(getState());
      dispatch(Pending.setPending(pendingId));
      let isSuccess = true;

      try {
        const response = await API.Account.upgradePaidSubscription({ plan_id });

        const { status, decline_code, client_secret } = response.body;

        // User needs to authenticate payment method. This should only happen
        // for credit card payment methods
        if (status === 'requires_action') {
          await scriptLoader(AppConfig.stripe.url);

          const stripe = window.Stripe(AppConfig.stripe.publishableKey, {
            apiVersion: AppConfig.stripe.apiVersion,
          });

          const { error } = await stripe.confirmCardPayment(client_secret);

          if (error) {
            dispatch(
              Notification.setNotification(
                'error',
                'complete_payment_exception'
              )
            );
          }
        }

        // User needs to try with a different payment method
        // We could show more specific error messages below. Currently only
        // showing special message when user needs to contact their bank.
        if (status === 'requires_payment_method') {
          const error_message =
            decline_code in CONTACT_BANK_ERROR_CODES
              ? 'declined_contact_bank_error'
              : 'source_id_error';

          dispatch(Notification.setNotification('error', error_message));
          // TODO: Not redirecting until we have implementing the upgrade in
          // the checkout flow
          // Router.navigateTo('/payment/methods', false, {
          //   search: 'upgrade=true',
          // });
        }

        await Promise.all([
          dispatch(UserActions.getUser()),
          dispatch(this.getSubscription(account.id, account.subscription.id)),
          dispatch(this.getUnpaidInvoices()),
        ]);
      } catch (error) {
        isSuccess = false;
        dispatch(Notification.setNotification('error', 'something_went_wrong'));
      }

      dispatch(Pending.resetPending(pendingId));

      return isSuccess;
    };
  },
});
